// CTV-611: minimal Waymark "rework" client for the edit-commercial panel.
// Deliberately NOT a lift of WaymarkProvider into the modal context: the
// provider owns the full account/generation lifecycle for the wizard, while
// this hook only needs to reopen an already-rendered video so the buyer can
// fix a rejected commercial. Flow: POST /waymark/login-for-creative mints a
// login token plus the source draft's videoId, we bootstrap the SDK exactly
// like the provider does (same constructor + createAccount/loginAccount
// branch), open the editor on that video, and when the broadcast-quality
// render lands we fetch it as a Blob, wrap it in a File, and hand it to the
// caller. The SDK module is imported lazily so it stays out of the modal's
// initial bundle (and out of jsdom test runs).

import React from 'react';
import { apiClient, ApiError } from '../../../lib/apiClient';
import { validateAdSpecs, formatAdSpecViolations } from '../shared/validateAdSpecs';
import type { IssueTokenResponse } from './types';

type WaymarkInstance = InstanceType<(typeof import('@waymark/waymark-sdk'))['default']>;

// Response of POST /waymark/login-for-creative: the /waymark/token shape plus
// the videoId recovered from the source draft's persisted state.
interface LoginForCreativeResponse extends IssueTokenResponse {
  videoId: string;
}

export interface UseWaymarkReworkArgs {
  /**
   * Called with the broadcast-quality render once the buyer clicks Finish in
   * the editor and the file passes the ad-spec gate. `sourceDraftId` echoes
   * the value passed to `openRework` so the caller can stamp the new
   * creative's draft pointer (addCreatives[].sourceDraftId).
   */
  onFileReady: (file: File, sourceDraftId: string) => void;
}

export interface UseWaymarkReworkResult {
  openRework: (args: { creativeId: string; sourceDraftId: string }) => Promise<void>;
  /** True while the token round-trip + SDK bootstrap is in flight. */
  isBusy: boolean;
  isEditorOpen: boolean;
  /**
   * Creatives whose draft can no longer be reopened (409
   * waymark_video_unavailable, or 404). Callers hide/disable the Waymark
   * action for these and point the buyer at the upload path instead.
   */
  unavailableCreativeIds: ReadonlySet<string>;
  error: string | null;
}

// Mirrors WaymarkProvider's hidden 1x1 off-screen container styling.
const HIDDEN_CONTAINER_STYLE: Partial<CSSStyleDeclaration> = {
  position: 'absolute',
  left: '-99999px',
  top: '-99999px',
  transform: 'none',
  width: '1px',
  height: '1px',
  zIndex: '-1',
  pointerEvents: 'none',
};

// Mirrors WaymarkProvider's centered editor window (~90% viewport over a
// dimmed backdrop, not a fullscreen takeover).
const OPEN_CONTAINER_STYLE: Partial<CSSStyleDeclaration> = {
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'min(1100px, 92vw)',
  height: 'min(720px, 88vh)',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 25px 60px -15px rgba(15, 23, 42, 0.45)',
  background: '#fff',
  zIndex: '1000',
  pointerEvents: 'auto',
};

function isVideoUnavailable(body: unknown): boolean {
  return (
    typeof body === 'object' &&
    body !== null &&
    (body as { code?: unknown }).code === 'waymark_video_unavailable'
  );
}

export function useWaymarkRework({ onFileReady }: UseWaymarkReworkArgs): UseWaymarkReworkResult {
  const [isBusy, setIsBusy] = React.useState(false);
  const [isEditorOpen, setIsEditorOpen] = React.useState(false);
  const [unavailableCreativeIds, setUnavailableCreativeIds] = React.useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [error, setError] = React.useState<string | null>(null);

  const sdkRef = React.useRef<WaymarkInstance | null>(null);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const backdropRef = React.useRef<HTMLDivElement | null>(null);
  // videoRendered can fire more than once for the same broadcast-quality URL
  // (email-format fires are already filtered out below); process each URL once.
  const processedUrlRef = React.useRef<string | null>(null);
  const onFileReadyRef = React.useRef(onFileReady);
  onFileReadyRef.current = onFileReady;

  const hideEditor = React.useCallback(() => {
    if (backdropRef.current) {
      backdropRef.current.remove();
      backdropRef.current = null;
    }
    if (containerRef.current) {
      Object.assign(containerRef.current.style, HIDDEN_CONTAINER_STYLE);
    }
    setIsEditorOpen(false);
  }, []);

  const closeEditor = React.useCallback(async () => {
    try {
      await sdkRef.current?.close();
    } catch {
      // The editor iframe may already be gone; hiding the overlay is what matters.
    }
    hideEditor();
  }, [hideEditor]);

  const showEditor = React.useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    if (!backdropRef.current) {
      const backdrop = document.createElement('div');
      backdrop.setAttribute('aria-hidden', 'true');
      Object.assign(backdrop.style, {
        position: 'fixed',
        inset: '0',
        background: 'rgba(15, 23, 42, 0.55)',
        zIndex: '999',
      });
      backdrop.addEventListener('click', () => {
        void closeEditor();
      });
      document.body.appendChild(backdrop);
      backdropRef.current = backdrop;
    }
    Object.assign(container.style, OPEN_CONTAINER_STYLE);
    setIsEditorOpen(true);
  }, [closeEditor]);

  const ensureContainer = React.useCallback((): HTMLDivElement => {
    if (containerRef.current) return containerRef.current;
    const el = document.createElement('div');
    el.setAttribute('data-waymark-rework-container', '');
    Object.assign(el.style, HIDDEN_CONTAINER_STYLE);
    document.body.appendChild(el);
    containerRef.current = el;
    return el;
  }, []);

  // Teardown on unmount: cleanup the SDK and remove the imperative DOM nodes.
  React.useEffect(() => {
    return () => {
      const sdk = sdkRef.current;
      sdkRef.current = null;
      if (sdk) {
        void sdk.cleanup().catch(() => {
          /* noop */
        });
      }
      backdropRef.current?.remove();
      backdropRef.current = null;
      containerRef.current?.remove();
      containerRef.current = null;
    };
  }, []);

  const handleRendered = React.useCallback(
    async (renderedUrl: string, videoId: string, sourceDraftId: string) => {
      if (processedUrlRef.current === renderedUrl) return;
      processedUrlRef.current = renderedUrl;
      try {
        const res = await fetch(renderedUrl);
        if (!res.ok) {
          setError(`Could not download the reworked video (${res.status}). Please try again.`);
          return;
        }
        const blob = await res.blob();
        // Same client-side ad-spec gate the wizard's handleCommitWaymark
        // applies before a Waymark render enters the upload pipeline.
        const specs = await validateAdSpecs(blob);
        if (!specs.ok) {
          setError(formatAdSpecViolations(specs.violations));
          return;
        }
        const file = new File([blob], `waymark-${videoId}.mp4`, { type: 'video/mp4' });
        onFileReadyRef.current(file, sourceDraftId);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not download the reworked video');
      }
    },
    [],
  );

  const openRework = React.useCallback(
    async ({ creativeId, sourceDraftId }: { creativeId: string; sourceDraftId: string }) => {
      setError(null);
      setIsBusy(true);
      try {
        const auth = await apiClient.post<LoginForCreativeResponse>('/waymark/login-for-creative', {
          creativeId,
        });

        const { default: Waymark } = await import('@waymark/waymark-sdk');
        const container = ensureContainer();

        // One SDK per rework: each creative's draft can belong to a different
        // Waymark account, so a cached login can't be reused across creatives.
        const prior = sdkRef.current;
        if (prior) {
          sdkRef.current = null;
          try {
            await prior.cleanup();
          } catch {
            /* noop */
          }
        }

        const sdk = new Waymark(auth.partnerID, {
          authMode: 'jwt',
          environment: auth.environment,
          domElement: container,
        });
        if (auth.mode === 'create') {
          await sdk.createAccount(auth.token);
        } else {
          await sdk.loginAccount(auth.token);
        }

        sdk.on('videoRendered', (videoData) => {
          // Waymark renders both email and broadcast_quality formats; only the
          // broadcast-quality file is usable as a commercial (same guard as
          // WaymarkProvider's videoRendered handler).
          const renders = videoData.renders ?? [];
          const rendered = renders.find(
            (r) => r.status === 'succeeded' && r.format === 'broadcast_quality',
          );
          if (!rendered?.url) return;
          void handleRendered(rendered.url, videoData.id, sourceDraftId);
          void closeEditor();
        });
        sdk.on('editorExited', () => hideEditor());
        sdkRef.current = sdk;

        await sdk.openEditorForVideo(auth.videoId);
        showEditor();
      } catch (e) {
        if (
          e instanceof ApiError &&
          (e.status === 404 || (e.status === 409 && isVideoUnavailable(e.body)))
        ) {
          setUnavailableCreativeIds((prev) => {
            const next = new Set(prev);
            next.add(creativeId);
            return next;
          });
        } else {
          setError(e instanceof Error ? e.message : 'Could not open the Waymark editor');
        }
        hideEditor();
      } finally {
        setIsBusy(false);
      }
    },
    [ensureContainer, handleRendered, closeEditor, hideEditor, showEditor],
  );

  return { openRework, isBusy, isEditorOpen, unavailableCreativeIds, error };
}
